import { refreshSession } from '@/lib/api/client';
import { isTorBlockedResponse, markTorBlocked } from '@/lib/api/tor-block';
import { getAccessToken } from '@/lib/auth/token-store';
import { env } from '@/lib/env';
import { traceHeaders } from '@/lib/observability/tracing';
import type { BellConnection } from '../types';

/** While the stream is down every tab re-reads the snapshot this often (AC7.5). */
export const POLL_INTERVAL_MS = 5 * 60_000;
/** A refused stream (429 BELL_STREAM_LIMIT) is retried this much later, never sooner. */
const LIMIT_RETRY_MS = 5 * 60_000;
const BACKOFF_BASE_MS = 1_000;
const BACKOFF_CAP_MS = 30_000;
/**
 * The server pings every 25 s, so this long without a single byte means the
 * connection is half-open (a dead proxy, a sleeping laptop): abort it and
 * reconnect rather than show "Live" forever with no polling behind it.
 */
export const IDLE_TIMEOUT_MS = 60_000;
/**
 * After `expiring` the stream is already closed while the session refreshes.
 * A refresh that takes longer than this (a stalled `/auth/refresh`, a long
 * cross-tab lock queue) reports `reconnecting` so polling covers the gap.
 */
export const EXPIRY_REFRESH_GRACE_MS = 5_000;

export interface BellStreamHandlers {
  /** A nudge arrived: something on this account's bell changed. */
  onChanged(): void;
  /** Reported on change only; a planned expiry reconnect reports nothing. */
  onStatus(status: BellConnection): void;
}

/** The stream's collaborators; tests pass fakes, the app uses the defaults. */
export interface BellStreamDeps {
  url: string;
  fetch: (url: string, init: RequestInit) => Promise<Response>;
  getToken: () => string | null;
  refreshSession: () => Promise<boolean>;
  /** A uniform draw in [0, 1) for backoff jitter. */
  random: () => number;
  /** Abort a connection that has received no bytes for this long. */
  idleTimeoutMs: number;
}

/** What a finished connection asks for next (`undefined`: already handled). */
type Next = 'refresh-then-retry' | 'expiring' | undefined;

export interface BellStream {
  /** Abort the stream and every pending retry (sign-out, leadership lost). */
  stop(): void;
}

/**
 * The retry delay after `attempt` consecutive failures: a ceiling of 1 s
 * doubling to a 30 s cap, with equal jitter (half the ceiling fixed, half
 * random) so a whole tenant dropped by one API restart does not reconnect in
 * lockstep, yet no client ever retries sooner than half its ceiling.
 */
export function backoffDelay(attempt: number, random: () => number = Math.random): number {
  const ceiling = Math.min(BACKOFF_BASE_MS * 2 ** attempt, BACKOFF_CAP_MS);
  return Math.round(ceiling / 2 + random() * (ceiling / 2));
}

/**
 * Open the bell's server-sent event stream (`GET /notifications/stream`, R7).
 *
 * It is read with `fetch` rather than `EventSource` because the session is an
 * in-memory Bearer token and `EventSource` cannot send an Authorization header.
 * Failure handling follows AC7.5:
 * - an unexpected error or close reports `reconnecting` and retries with backoff;
 * - a 429 reports `reconnecting` and retries after 5 minutes, never in a loop;
 * - `expiring` (the server's planned close before the token's `exp`) refreshes
 *   the session and reconnects at once, without reporting anything; if that
 *   refresh fails it reports `reconnecting` and backs off, and if it takes
 *   longer than `EXPIRY_REFRESH_GRACE_MS` it reports `reconnecting` meanwhile;
 * - a 401 refreshes once, then follows the backoff;
 * - no bytes for `IDLE_TIMEOUT_MS` (pings come every 25 s) aborts the
 *   connection as half-open and follows the backoff.
 * It reports `live` only once the server's `ready` frame arrives.
 * A 403 or 204 means there is nothing to stream for this principal, so it
 * stops and reports `reconnecting`: never "Live", and the snapshot falls back
 * to polling. A 403 `TOR_BLOCKED` instead marks the Tor block (the app swaps
 * in the block page) and stops for good: retrying cannot succeed.
 */
export function startBellStream(handlers: BellStreamHandlers, overrides: Partial<BellStreamDeps> = {}): BellStream {
  const deps: BellStreamDeps = {
    url: `${env.VITE_API_URL}/notifications/stream`,
    fetch: (url, init) => fetch(url, init),
    getToken: getAccessToken,
    refreshSession,
    random: Math.random,
    idleTimeoutMs: IDLE_TIMEOUT_MS,
    ...overrides,
  };
  let stopped = false;
  let attempt = 0;
  let status: BellConnection | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let current: AbortController | null = null;

  const report = (next: BellConnection) => {
    if (status === next) return;
    status = next;
    handlers.onStatus(next);
  };
  const retryIn = (ms: number) => {
    timer = setTimeout(() => {
      timer = undefined;
      void connect(true);
    }, ms);
  };
  const fail = () => {
    if (stopped) return;
    report('reconnecting');
    retryIn(backoffDelay(attempt, deps.random));
    attempt += 1;
  };

  async function connect(mayRefresh: boolean): Promise<void> {
    if (stopped) return;
    const next = await watched(mayRefresh);
    if (next === 'refresh-then-retry') {
      if (await deps.refreshSession()) return connect(false);
      return fail();
    }
    if (next === 'expiring') {
      const grace = setTimeout(() => {
        if (!stopped) report('reconnecting');
      }, EXPIRY_REFRESH_GRACE_MS);
      const ok = await deps.refreshSession().catch(() => false);
      clearTimeout(grace);
      if (ok) return connect(true);
      return fail();
    }
  }

  /** One connection under the idle watchdog; says what to do once it has ended. */
  async function watched(mayRefresh: boolean): Promise<Next> {
    const controller = new AbortController();
    current = controller;
    // The watchdog covers the wait for headers too: a request that never
    // answers is as dead as a stream that stopped talking.
    let idle: ReturnType<typeof setTimeout> | undefined;
    const touch = () => {
      clearTimeout(idle);
      idle = setTimeout(() => controller.abort(), deps.idleTimeoutMs);
    };
    touch();
    try {
      return await attemptOnce(controller, touch, mayRefresh);
    } finally {
      clearTimeout(idle);
    }
  }

  async function attemptOnce(controller: AbortController, touch: () => void, mayRefresh: boolean): Promise<Next> {
    let res: Response;
    try {
      const headers: Record<string, string> = { accept: 'text/event-stream', ...traceHeaders() };
      const token = deps.getToken();
      if (token) headers['authorization'] = `Bearer ${token}`;
      res = await deps.fetch(deps.url, { method: 'GET', credentials: 'include', cache: 'no-store', headers, signal: controller.signal });
    } catch {
      return void fail();
    }
    if (stopped) return;
    if (res.status === 401) {
      if (mayRefresh) return 'refresh-then-retry';
      return void fail();
    }
    if (res.status === 429) {
      report('reconnecting');
      return void retryIn(LIMIT_RETRY_MS);
    }
    if (res.status === 403 && (await isTorBlockedResponse(res))) {
      markTorBlocked();
      stopped = true;
      return;
    }
    if (stopped) return;
    if (res.status === 204 || res.status === 403) return void report('reconnecting');
    if (!res.ok || !res.body) return void fail();

    const ending = await read(res.body, controller, touch);
    if (stopped) return;
    if (ending === 'expiring') return 'expiring';
    fail();
  }

  async function read(body: ReadableStream<Uint8Array>, controller: AbortController, touch: () => void): Promise<'closed' | 'expiring'> {
    const reader = body.getReader();
    const cancel = () => void reader.cancel().catch(() => undefined);
    controller.signal.addEventListener('abort', cancel);
    const decoder = new TextDecoder();
    let buffer = '';
    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) return 'closed';
        touch();
        const parsed = parseEvents(buffer + decoder.decode(value, { stream: true }));
        buffer = parsed.rest;
        for (const event of parsed.events) {
          if (event === 'ready') {
            attempt = 0;
            report('live');
          } else if (event === 'changed') {
            handlers.onChanged();
          } else if (event === 'expiring') {
            controller.abort();
            return 'expiring';
          }
        }
      }
    } catch {
      return 'closed';
    } finally {
      controller.signal.removeEventListener('abort', cancel);
    }
  }

  void connect(true);
  return {
    stop() {
      stopped = true;
      if (timer !== undefined) clearTimeout(timer);
      current?.abort();
    },
  };
}

/** A `() => void` that coalesces a burst of calls into one, `ms` after the first. */
export interface BurstDebounced {
  (): void;
  cancel(): void;
}

/**
 * One refetch per 1 s burst of nudges (AC7.4). The window is fixed from the
 * first call, not extended by later ones, so a steady stream of nudges still
 * refreshes every second rather than never.
 *
 * `jitterMs` adds a fresh random 0..jitterMs to each window, so the tabs and
 * browsers of a large tenant nudged by the same change do not all refetch in
 * the same instant.
 */
export function burstDebounce(fn: () => void, ms: number, jitterMs = 0, random: () => number = Math.random): BurstDebounced {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const call = () => {
    if (timer !== undefined) return;
    timer = setTimeout(() => {
      timer = undefined;
      fn();
    }, ms + Math.floor(random() * jitterMs));
  };
  return Object.assign(call, {
    cancel() {
      if (timer !== undefined) clearTimeout(timer);
      timer = undefined;
    },
  });
}

/**
 * Split complete SSE frames off `buffer` and return their event names (a frame
 * with no `event:` field is `message`; comment-only frames such as `: ping`
 * are skipped). The bell needs only the names: no item data travels on the
 * stream (AC7.2).
 */
function parseEvents(buffer: string): { events: string[]; rest: string } {
  const blocks = buffer.replace(/\r\n/g, '\n').split('\n\n');
  const rest = blocks.pop() ?? '';
  const events: string[] = [];
  for (const block of blocks) {
    let name: string | null = null;
    let hasField = false;
    for (const line of block.split('\n')) {
      if (line === '' || line.startsWith(':')) continue;
      hasField = true;
      const colon = line.indexOf(':');
      if (colon >= 0 && line.slice(0, colon) === 'event') name = line.slice(colon + 1).trimStart();
    }
    if (hasField) events.push(name ?? 'message');
  }
  return { events, rest };
}
